// Pricing Service for fetching real-time card prices from multiple sources
// Uses PokemonTCG.io API for raw card pricing (with optional API key for higher rate limits)
// Supports raw cards and graded cards (PSA, BGS, CGC, TAG)

export interface GradedPrice {
  grade: string; // e.g., "PSA 10", "BGS 9.5", "CGC 10"
  price: number;
  recentSales: RecentSale[];
  population?: number; // How many cards graded at this level
}

export interface RecentSale {
  date: string;
  price: number;
  condition?: string;
  grade?: string;
  source: string; // e.g., "eBay", "TCGPlayer", "PWCC"
}

export interface CardPricing {
  cardId: string;
  cardName: string;
  setId: string;
  lastUpdated: string;

  // Raw card pricing
  raw: {
    market: number;
    low: number;
    mid: number;
    high: number;
    recentSales: RecentSale[];
  };

  // Graded pricing by company
  psa: GradedPrice[];
  bgs: GradedPrice[]; // Beckett
  cgc: GradedPrice[];
  tag: GradedPrice[];

  // eBay listings (added for price history & metrics)
  soldListings?: Array<{
    title: string;
    price: number;
    condition: string;
    soldDate?: string;
    listingUrl: string;
    imageUrl?: string;
  }>;
  activeListings?: Array<{
    title: string;
    price: number;
    condition: string;
    listingUrl: string;
    imageUrl?: string;
  }>;
  averagePrice?: number;
}

// Cache for pricing data (expires after 1 hour)
const pricingCache = new Map<string, { data: CardPricing; expires: number }>();
const CACHE_DURATION = 60 * 60 * 1000; // 1 hour

/**
 * Fetch pricing data for a card from multiple sources
 */
export async function fetchCardPricing(
  cardId: string,
  cardName: string,
  setId: string,
  fallbackData?: any
): Promise<CardPricing> {
  // Check cache first
  const cached = pricingCache.get(cardId);
  if (cached && cached.expires > Date.now()) {
    return cached.data;
  }

  // Known custom sets that don't exist in the API
  const CUSTOM_SETS = ['me2'];

  try {
    // Only fetch from official API if it's not a custom set
    const shouldFetchApi = !CUSTOM_SETS.includes(setId);
    
    // Fetch from multiple sources in parallel
    const [pokemonTcgData, ebayData, pwccData] = await Promise.allSettled([
      shouldFetchApi ? fetchPokemonTCGPricing(cardId) : Promise.resolve(null),
      fetchEbayPricing(cardName, setId),
      fetchPWCCPricing(cardName, setId),
    ]);

    let tcgData = pokemonTcgData.status === 'fulfilled' ? pokemonTcgData.value : null;

    // Use fallback data if API failed or returned explicit null
    if (!tcgData && fallbackData) {
      console.log('Using fallback pricing data for:', cardId);
      tcgData = parseFallbackPricing(fallbackData, cardId);
    }

    // Merge data from all sources
    const pricing = mergePricingData(
      cardId,
      cardName,
      setId,
      tcgData,
      ebayData.status === 'fulfilled' ? ebayData.value : null,
      pwccData.status === 'fulfilled' ? pwccData.value : null
    );

    // Cache the result
    pricingCache.set(cardId, {
      data: pricing,
      expires: Date.now() + CACHE_DURATION,
    });

    return pricing;
  } catch (error) {
    console.error('Error fetching card pricing:', error);
    // Return default/empty pricing
    return getDefaultPricing(cardId, cardName, setId);
  }
}

/**
 * Fetch pricing from PokemonTCG.io API
 * This API is free and doesn't require an API key, but using one provides higher rate limits
 */
async function fetchPokemonTCGPricing(cardId: string): Promise<any> {
  try {
    const url = `https://api.pokemontcg.io/v2/cards/${cardId}`;
    console.log('Fetching pricing for card:', cardId);
    console.log('API URL:', url);

    const headers: HeadersInit = {};
    const apiKey = process.env.REACT_APP_POKEMONTCG_KEY;
    console.log('DEBUG: Checking API Key env var:', apiKey ? 'Present' : 'Missing');
    if (apiKey) {
      headers['X-Api-Key'] = apiKey;
      console.log('Using API key for request');
    }

    const response = await fetch(url, { headers });

    if (!response.ok) {
      // Don't log error for 404 (card not found), which is expected for custom/local cards
      if (response.status !== 404) {
        console.error(
          'PokemonTCG API error:',
          response.status,
          response.statusText
        );
        console.error('Failed URL:', url);
      } else {
        console.warn(`Card not found in API: ${cardId}`);
      }
      return null;
    }

    const data = await response.json();
    console.log('API Response:', data);
    const card = data.data;

    // Extract TCGPlayer pricing from the response
    const tcgplayerPrices = card?.tcgplayer?.prices;

    if (!tcgplayerPrices) {
      console.warn('No TCGPlayer pricing found for card:', cardId);
      return null;
    }

    // The API can have different price types: normal, holofoil, reverseHolofoil, 1stEditionHolofoil, etc.
    // We'll prioritize holofoil, then normal, then take the first available
    let priceData =
      tcgplayerPrices.holofoil ||
      tcgplayerPrices.normal ||
      tcgplayerPrices.reverseHolofoil ||
      tcgplayerPrices['1stEditionHolofoil'] ||
      Object.values(tcgplayerPrices)[0];

    if (!priceData) {
      console.warn('No price data found in tcgplayer prices:', tcgplayerPrices);
      return null;
    }

    console.log('Found pricing:', priceData);

    return {
      raw: {
        market: priceData.market || priceData.mid || 0,
        low: priceData.low || 0,
        mid: priceData.mid || 0,
        high: priceData.high || 0,
        recentSales: [
          {
            date: card.tcgplayer?.updatedAt || new Date().toISOString(),
            price: priceData.market || priceData.mid || 0,
            source: 'PokemonTCG.io / TCGPlayer',
          },
        ],
      },
    };
  } catch (error) {
    console.error('Error fetching from PokemonTCG.io:', error);
    return null;
  }
}

/**
 * Fetch pricing from backend eBay integration
 * Calls backend API which securely fetches from eBay
 */
async function fetchEbayPricing(cardName: string, setId: string): Promise<any> {
    try {
      const { API_BASE_URL, getAuthHeaders } = await import('./pokemonCardOwnershipService');
      const cardId = `${setId}-${cardName.split(' ').join('-')}`;
      
      console.log('[FRONTEND] Fetching eBay pricing from backend for:', cardId);
      
      // Get auth headers
      const headers = await getAuthHeaders();
      
      const cardNumber = cardId.split('-')[1] || '';
      
      // eBay sellers use One Piece set IDs (OP01, EB01) but almost never use Pokemon internal IDs (neo1, swsh1)
      const isOnePiece = /^(OP|EB|PRB|ST)\d*/i.test(setId);
      
      const response = await fetch(`${API_BASE_URL}/api/public/pricing/search`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          cardName,
          cardNumber,
          setName: isOnePiece ? setId : '',
          game: isOnePiece ? 'onepiece' : 'pokemon'
        }),
      });

      if (!response.ok) {
        console.error('Backend pricing API error:', response.status, response.statusText);
        return { soldListings: [], activeListings: [], psa: [], bgs: [], cgc: [], tag: [] };
      }

      const data = await response.json();
      const ebayData = data.ebay;
      
      console.log(`[FRONTEND] Received ${ebayData.soldListings?.length || 0} sold listings, ${ebayData.activeListings?.length || 0} active listings`);

      // Process listings to extract graded cards
      const results: any = {
        soldListings: ebayData.soldListings || [],
        activeListings: ebayData.activeListings || [],
        averagePrice: ebayData.averagePrice || 0,
        psa: [],
        bgs: [],
        cgc: [],
        tag: [],
      };

      // Combine sold and active listings for grading analysis
      const allListings = [
        ...(ebayData.soldListings || []),
        ...(ebayData.activeListings || []),
      ];

      // Helper to process listings into graded cards
      const processListings = (company: string, regex: RegExp) => {
        const companyListings = allListings.filter((item: any) =>
          regex.test(item.title)
        );

        const gradeMap = new Map<string, any[]>();
        
        companyListings.forEach((item: any) => {
          const match = item.title.match(regex);
          const gradeNum = match ? match[1] || match[2] : '?';
          const fullGrade = `${company.toUpperCase()} ${gradeNum}`;
          
          if (!gradeMap.has(fullGrade)) {
             gradeMap.set(fullGrade, []);
          }
          
          gradeMap.get(fullGrade)?.push({
            date: item.soldDate || item.listingDate || new Date().toISOString(),
            price: parseFloat(item.price),
            grade: fullGrade,
            source: 'eBay',
            title: item.title,
            url: item.listingUrl
          });
        });

        return Array.from(gradeMap.entries()).map(([grade, sales]) => {
            const prices = sales.map((s: any) => s.price);
            const avgPrice = prices.reduce((a: number, b: number) => a + b, 0) / prices.length;
            
            return {
                grade,
                price: avgPrice,
                recentSales: sales,
                population: undefined
            };
        });
      };

      // Extract graded cards
      results.psa = processListings('psa', /PSA\s*?(\d+(\.\d+)?)/i);
      results.bgs = processListings('bgs', /BGS\s*?(\d+(\.\d+)?)/i);
      results.cgc = processListings('cgc', /CGC\s*?(\d+(\.\d+)?)/i);
      results.tag = processListings('tag', /TAG\s*?(\d+(\.\d+)?)/i);
      
      return results;

    } catch (error) {
      console.error('Error in fetchEbayPricing:', error);
       return { soldListings: [], activeListings: [], psa: [], bgs: [], cgc: [], tag: [] };
    }
}

/**
 * Fetch pricing from PWCC (auction house for high-end cards)
 */
async function fetchPWCCPricing(cardName: string, setId: string): Promise<any> {
  // TODO: Implement PWCC scraping or API if available
  return null;
}

/**
 * Merge pricing data from multiple sources
 */
function mergePricingData(
  cardId: string,
  cardName: string,
  setId: string,
  pokemonTcg: any,
  ebay: any,
  pwcc: any
): CardPricing {
  const pricing: CardPricing = {
    cardId,
    cardName,
    setId,
    lastUpdated: new Date().toISOString(),
    raw: {
      market: pokemonTcg?.raw?.market || 0,
      low: pokemonTcg?.raw?.low || 0,
      mid: pokemonTcg?.raw?.mid || 0,
      high: pokemonTcg?.raw?.high || 0,
      recentSales: pokemonTcg?.raw?.recentSales || [],
    },
    psa: ebay?.psa || [],
    bgs: ebay?.bgs || [],
    cgc: ebay?.cgc || [],
    tag: ebay?.tag || [],
  };

  return pricing;
}

/**
 * Get default/empty pricing structure
 */
function getDefaultPricing(
  cardId: string,
  cardName: string,
  setId: string
): CardPricing {
  return {
    cardId,
    cardName,
    setId,
    lastUpdated: new Date().toISOString(),
    raw: {
      market: 0,
      low: 0,
      mid: 0,
      high: 0,
      recentSales: [],
    },
    psa: [],
    bgs: [],
    cgc: [],
    tag: [],
  };
}

/**
 * Clear pricing cache (useful for manual refresh)
 */
export function clearPricingCache() {
  pricingCache.clear();
}

/**
 * Parse fallback TCGPlayer data from local JSON
 */
function parseFallbackPricing(tcgplayerData: any, cardId: string): any {
  if (!tcgplayerData || !tcgplayerData.prices) {
    return null;
  }

  const prices = tcgplayerData.prices;
  
  // Prioritize price types similar to API fetch
  const priceData =
    prices.holofoil ||
    prices.normal ||
    prices.reverseHolofoil ||
    prices['1stEditionHolofoil'] ||
    Object.values(prices)[0];

  if (!priceData) {
    return null;
  }

  return {
    raw: {
      market: priceData.market || priceData.mid || 0,
      low: priceData.low || 0,
      mid: priceData.mid || 0,
      high: priceData.high || 0,
      recentSales: [
        {
          date: tcgplayerData.updatedAt || new Date().toISOString(),
          price: priceData.market || priceData.mid || 0,
          source: 'Local Data / TCGPlayer',
        },
      ],
    },
  };
}

/**
 * Batch fetch pricing for multiple cards
 */
export async function fetchBatchPricing(
  cards: Array<{ id: string; name: string; setId: string }>
): Promise<Map<string, CardPricing>> {
  const results = new Map<string, CardPricing>();

  // Fetch in batches of 10 to avoid rate limiting
  const batchSize = 10;
  for (let i = 0; i < cards.length; i += batchSize) {
    const batch = cards.slice(i, i + batchSize);
    const promises = batch.map((card) =>
      fetchCardPricing(card.id, card.name, card.setId)
    );
    const batchResults = await Promise.allSettled(promises);

    batchResults.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        results.set(batch[index].id, result.value);
      }
    });

    // Rate limiting delay
    if (i + batchSize < cards.length) {
      await new Promise((resolve) => setTimeout(resolve, 1000));
    }
  }

  return results;
}

/**
 * Fetch top expensive cards for a specific Pokemon set
 * Used for detailed market highlights
 */
export async function fetchTopPokemonCards(setId: string, limit: number = 5): Promise<any[]> {
  try {
    // Simplify sort to just holofoil market for now, as it covers most hits (Alt Arts, SIRs)
    // Comma-separated sorts might be causing issues with URL encoding or API parsing
    const url = `https://api.pokemontcg.io/v2/cards?q=set.id:${setId}&orderBy=-tcgplayer.prices.holofoil.market&pageSize=${limit}`;
    
    // ... (keep auth headers) ...
    const headers: HeadersInit = {};
    const apiKey = process.env.REACT_APP_POKEMONTCG_KEY;
    if (apiKey) {
      headers['X-Api-Key'] = apiKey;
    }

    const response = await fetch(url, { headers });
    if (!response.ok) {
        console.warn(`Failed to fetch top cards for set ${setId}: ${response.status}`);
        return [];
    }
    
    const data = await response.json();
    return data.data || [];
  } catch (error) {
    console.error('Error fetching top pokemon cards:', error);
    return [];
  }
}
